import { List, Stack, Text } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { CHANGE_TYPES, type Change, type ChangeType } from "@models/releaseNotes";
import { useLocalized } from "@utils/localizedText";
import "./ChangeList.scss";

interface Props {
  changes: Change[];
}

/**
 * A release's changes, grouped under New / Improved / Fixed.
 *
 * Grouped rather than a flat list with a badge per row: that is how product
 * changelogs are read. A reader scanning for "what can I do now" wants the New
 * section, not to filter coloured pills out of a mixed list. Empty groups are
 * dropped, and the order is fixed so the same heading never moves between
 * entries.
 *
 * The marker itself is declared in ChangeList.scss. Mantine's List leaves it to
 * the browser default, which any host reset removes; see that file for why.
 *
 * Plain text throughout. No markdown renderer and no `dangerouslySetInnerHTML`:
 * an HTML sink mounted inside the shell would be the worst-placed one in the
 * platform.
 */
export const ChangeList = ({ changes }: Props) => {
  const { t } = useTranslation();
  const { pick } = useLocalized();

  const groups = CHANGE_TYPES.map((type) => ({
    type,
    items: changes.filter((change) => change.type === type),
  })).filter((group) => group.items.length > 0);

  if (groups.length === 0) return null;

  return (
    <Stack gap="md" className="change-list">
      {groups.map(({ type, items }) => (
        <Stack key={type} gap={6}>
          <Text
            size="xs"
            fw={700}
            tt="uppercase"
            c={TONE[type]}
            className="change-list__heading"
          >
            {t(`releases.changeType.${type}`)}
          </Text>

          <List size="sm" spacing={4} className="change-list__items">
            {items.map((change, index) => (
              <List.Item key={index}>{pick(change.text)}</List.Item>
            ))}
          </List>
        </Stack>
      ))}
    </Stack>
  );
};

const TONE: Record<ChangeType, string> = {
  new: "green",
  improved: "blue",
  fixed: "dimmed",
};
